import { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Stack,
  Button,
  Tooltip,
  Drawer,
  Divider,
  IconButton,
  Typography,
  Alert,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useForm } from 'react-hook-form';
import Iconify from '../../../components/iconify';
import Scrollbar from '../../../components/scrollbar';
import FormProvider, { RHFTextField } from '../../../components/hook-form';
import useUnsavedChanges from '../../../hooks/useUnsavedChanges';
import UnsavedChangesDialog from '../../../components/unsaved-changes-dialog';

const StyledLabel = styled('span')(({ theme }) => ({
  ...theme.typography.caption,
  color: theme.palette.text.secondary,
  fontSize: 14,
  lineHeight: 2.5,
}));

export default function CustomersModal({
  customer,
  isEdit,
  createCustomer,
  updateCustomer,
  openModal,
  onCloseModal,
  onOpenDialog,
  setSuccess, // Added new prop for handling success messages
  viewMode = false, // New prop to indicate view-only mode (e.g., from bookings modal)
}) {
  const defaultValues = useMemo(
    () => ({
      firstName: customer?.firstName || '',
      lastName: customer?.lastName || '',
      email: customer?.email || '',
      phoneNumber: customer?.phoneNumber || '',
      note: customer?.note || '',
    }),
    [customer]
  );

  const methods = useForm({ defaultValues });
  const [formErrors, setFormErrors] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [unsavedChangesDialog, setUnsavedChangesDialog] = useState({ open: false, data: null });
  
  const {
    reset,
    handleSubmit,
    formState: { isSubmitting, isDirty, isSubmitSuccessful },
  } = methods;

  // Unsaved changes hook
  const { handleClose } = useUnsavedChanges({
    formState: { isDirty, isSubmitSuccessful },
    onUnsavedChanges: (data) => {
      setUnsavedChangesDialog({ open: true, data });
    },
    isOpen: openModal
  });

  useEffect(() => {
    reset(defaultValues);
  }, [isEdit, customer, reset, defaultValues]);

  const onSubmit = async (data) => {
    if (formErrors) {
      setFormErrors(null);
    }

    try {
      setIsLoading(true);

      if (isEdit) {
        await updateCustomer(data);
      } else {
        await createCustomer(data);
      }

      // Close modal directly after successful save without unsaved changes check
      reset();
      onCloseModal();
      setUnsavedChangesDialog({ open: false, data: null });
    } catch (error) {
      // Handle structured error format
      let errorMessage = 'An error occurred. Please try again.';
      
      if (error.response?.data?.messages) {
        errorMessage = error.response.data.messages.join('\n');
      } else if (error.messages) {
        errorMessage = error.messages.join('\n');
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setFormErrors({ message: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle modal close with unsaved changes check
  const handleCloseModal = () => {
    handleClose(() => {
      onCloseModal();
      setUnsavedChangesDialog({ open: false, data: null });
    });
  };

  // Handle unsaved changes dialog actions
  const handleUnsavedChangesDiscard = () => {
    if (unsavedChangesDialog.data?.onDiscard) {
      unsavedChangesDialog.data.onDiscard();
    }
    setUnsavedChangesDialog({ open: false, data: null });
  };

  const handleUnsavedChangesCancel = () => {
    setUnsavedChangesDialog({ open: false, data: null });
  };

  return (
    <>
    <Drawer
      anchor="right"
      open={openModal}
      onClose={handleCloseModal}
      PaperProps={{
        sx: {
          width: {
            xs: 1,
            sm: 480,
          },
        },
      }}
    >
      {!!formErrors && <Alert severity="error">{formErrors.message}</Alert>}

      <ToolBar>
        {isEdit && (
          <Tooltip title="Delete">
            <IconButton size="small" onClick={onOpenDialog}>
              <Iconify icon="eva:trash-2-outline" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Close">
          <IconButton onClick={handleCloseModal}>
            <Iconify icon="eva:close-fill" />
          </IconButton>
        </Tooltip>
      </ToolBar>

      <Divider />

      <Stack sx={{ p: 2 }}>
        <Typography variant="h5">
          {viewMode ? 'View/Edit Customer' : !isEdit ? 'Create Customer' : 'Update Customer'}
        </Typography>
      </Stack>

      <Scrollbar>
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <Stack sx={{ p: 2 }}>
            <Box
              rowGap={3}
              columnGap={2}
              display="grid"
              gridTemplateColumns={{
                xs: 'repeat(1, 1fr)',
                sm: 'repeat(2, 1fr)',
              }}
            >
              <Stack>
                <StyledLabel>First Name:</StyledLabel>
                <RHFTextField name="firstName" size="small" />
              </Stack>

              <Stack>
                <StyledLabel>Last Name:</StyledLabel>
                <RHFTextField name="lastName" size="small" />
              </Stack>
              <Stack>
                <StyledLabel>Email:</StyledLabel>
                <RHFTextField name="email" size="small" />
              </Stack>
              <Stack>
                <StyledLabel>Phone:</StyledLabel>
                <RHFTextField name="phoneNumber" size="small" />
              </Stack>
            </Box>
            <Stack sx={{ mt: 3, mb: 3 }}>
              <StyledLabel>Note:</StyledLabel>
              <RHFTextField multiline name="note" rows={4} size="small" />
            </Stack>
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            justifyContent="flex-end"
            flexGrow={1}
            mr={2}
            mb={2}
          >
            <Button
              size="medium"
              color="inherit"
              variant="outlined"
              onClick={handleCloseModal}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="medium"
              variant="contained"
              disabled={isLoading || isSubmitting}
              sx={{
                '&:disabled': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  opacity: 0.7
                }
              }}
            >
              {isLoading || isSubmitting ? (
                <>
                  <Box 
                    component="span" 
                    sx={{ 
                      display: 'inline-block',
                      width: 20,
                      height: 20,
                      mr: 1,
                      borderRadius: '50%',
                      borderTop: '2px solid currentColor',
                      borderRight: '2px solid transparent',
                      animation: 'spin 0.75s linear infinite',
                      '@keyframes spin': {
                        '0%': {
                          transform: 'rotate(0deg)',
                        },
                        '100%': {
                          transform: 'rotate(360deg)',
                        },
                      },
                    }} 
                  />
                  Processing...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </Stack>
        </FormProvider>
      </Scrollbar>
    </Drawer>

    {/* Unsaved Changes Dialog */}
    <UnsavedChangesDialog
      open={unsavedChangesDialog.open}
      onClose={handleUnsavedChangesCancel}
      onDiscard={handleUnsavedChangesDiscard}
      isSaving={isLoading || isSubmitting}
      title="Unsaved Customer Changes"
      message="You have unsaved changes to this customer. What would you like to do?"
    />
  </>
  );
}

function ToolBar({ children }) {
  return (
    <Stack pt={3} pb={3} direction="row" alignItems="center">
      <Stack
        direction="row"
        spacing={1}
        justifyContent="flex-end"
        flexGrow={1}
        pr={2}
      >
        {children}
      </Stack>
    </Stack>
  );
}